/**
 * Coupon Creation Page V2 - Migrated to use generated hooks
 *
 * This is an example migration showing how to convert a form submission
 * component from manual API calls to generated hooks.
 *
 * Key changes:
 * - Uses generated useCreateCoupon and useUpdateCoupon hooks
 * - Uses generated validation from coupon.validation
 * - Removes manual error/loading state management
 * - Simplifies success/error handling
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from '@/i18n';
import { Save, Eye, CheckCircle, AlertCircle, ArrowLeft, Users, ArrowRight } from 'lucide-react';
import { useMerchantCoupons } from '@/hooks-v2/aggregator';
import { useCurrentMerchant } from '@/hooks-v2/context';
import { useMerchantCouponActions } from '@/hooks-v2/coordinator/useMerchantCouponActions';
import { useMemberSelection } from '@/hooks-v2/useMemberSelection';
import {
	CouponTypeEnum,
	BasisPointsService,
	dollarsToCents,
	centsToDollars,
	TimestampService,
	formatForDisplay,
} from '@zlinkx/shared-v2';
import type { CouponType } from '@zlinkx/shared-v2';
import type { TransformedMerchantCoupon } from '@/utils/api-transformers';
import { Numeric } from '@/utils/numeric';
import type { CouponFormData, CouponFormErrors, SupportedLanguage } from '../../types/ui.types';
import { LanguageTabs, BackgroundGallery, MemberSelectionList } from '../../components/merchant/coupon';

const DATE_ONLY_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const CouponCreationV2: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isEditing = Boolean(id);

	const { selectedMerchantId } = useCurrentMerchant();

	const couponsData = useMerchantCoupons(selectedMerchantId ?? 0, {
		enabled: selectedMerchantId !== null,
	});
	const isLoading = couponsData.isLoading;

	// Use coordinator pattern for coupon actions
	const couponActions = useMerchantCouponActions();

	// Story 4.5: Step navigation state (1 = Create, 2 = Distribute)
	const [currentStep, setCurrentStep] = useState<1 | 2>(1);

	const [formData, setFormData] = useState<CouponFormData>({
		couponTitle: '',
		couponDescription: '',
		couponType: CouponTypeEnum.percentage,
		couponValue: 0,
		couponTextValue: '',
		couponCommissionPercentage: 0,
		couponExpiresAt: '',
		// Multi-language support (Story 4.2)
		titleFr: '',
		descriptionFr: '',
		titleZh: '',
		descriptionZh: '',
		enabledLanguages: ['en'],
		// Background image (Story 4.3)
		backgroundImageUrl: null,
	});

	// Story 4.5: Member selection hook
	const memberSelection = useMemberSelection(selectedMerchantId ?? 0);

	// Multi-language state (Story 4.2)
	const [activeLanguageTab, setActiveLanguageTab] = useState<SupportedLanguage>('en');

	// Language handlers (Story 4.2)
	const handleAddLanguage = (lang: 'fr' | 'zh') => {
		setFormData(prev => ({
			...prev,
			enabledLanguages: [...prev.enabledLanguages, lang],
		}));
		setActiveLanguageTab(lang);
	};

	const handleRemoveLanguage = (lang: 'fr' | 'zh') => {
		setFormData(prev => ({
			...prev,
			enabledLanguages: prev.enabledLanguages.filter(l => l !== lang),
			// Clear the form data for that language
			...(lang === 'fr' ? { titleFr: '', descriptionFr: '' } : {}),
			...(lang === 'zh' ? { titleZh: '', descriptionZh: '' } : {}),
		}));
		// Switch back to English tab
		setActiveLanguageTab('en');
		// Clear any translation errors
		setErrors(prev => ({
			...prev,
			...(lang === 'fr' ? { titleFr: undefined, descriptionFr: undefined } : {}),
			...(lang === 'zh' ? { titleZh: undefined, descriptionZh: undefined } : {}),
		}));
	};

	const [errors, setErrors] = useState<CouponFormErrors>({});
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);

	// Story 4.6: Confirmation modal state
	const [showConfirmModal, setShowConfirmModal] = useState(false);

	// Story 4.6 AC6: Success screen state
	const [distributionSuccess, setDistributionSuccess] = useState<{
		couponTitle: string;
		totalCoupons: number;
		memberCount: number;
	} | null>(null);

	const requiredLabel = (labelKey: string): string =>
		t('merchant.coupon.form.requiredLabel', { label: t(labelKey) });
	const requiredLabelWithLanguage = (labelKey: string, languageKey: string): string =>
		t('merchant.coupon.form.requiredLabelWithLanguage', {
			label: t(labelKey),
			language: t(languageKey),
		});
	const currencySymbol = t('merchant.coupon.form.currencySymbol');
	const parseExpirationDateInput = (value: string): Date | null => {
		const parsed = TimestampService.parseDateTimeInput(value);
		if (!TimestampService.isValidDate(parsed)) {
			return null;
		}

		if (DATE_ONLY_INPUT_PATTERN.test(value)) {
			parsed.setHours(23, 59, 59, 999);
		}

		return parsed;
	};

	const formatExpiryForPreview = (dateInput: string | undefined): string => {
		if (!dateInput) {
			return '';
		}

		const parsed = parseExpirationDateInput(dateInput);
		if (parsed === null) {
			return '';
		}

		return formatForDisplay(parsed, { style: 'short' });
	};

	const toDateInputValue = (value: string | null | undefined): string => {
		if (!value) {
			return '';
		}

		const parsed = TimestampService.parseDateTimeInput(value);
		if (!TimestampService.isValidDate(parsed)) {
			return '';
		}

		const defaults = TimestampService.getDisplayDefaults();
		const parts = new Intl.DateTimeFormat('en-US', {
			timeZone: defaults.timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).formatToParts(parsed);
		const year = parts.find(part => part.type === 'year')?.value;
		const month = parts.find(part => part.type === 'month')?.value;
		const day = parts.find(part => part.type === 'day')?.value;

		if (!year || !month || !day) {
			return '';
		}

		return `${year}-${month}-${day}`;
	};

	// Find existing coupon if editing
	const existingCoupon = useMemo(() => {
		if (!isEditing || !id) return null;
		const couponId = Numeric.parseInt(id);
		const coupons = Array.isArray(couponsData.data.coupons)
			? (couponsData.data.coupons as TransformedMerchantCoupon[])
			: [];
		return coupons.find(coupon => coupon.couponId === couponId) || null;
	}, [couponsData.data.coupons, id, isEditing]);

	// Load existing coupon data if editing
	// Using a flag to track if we've already loaded the data
	const [hasLoadedCoupon, setHasLoadedCoupon] = useState(false);

	useEffect(() => {
		// Only load the form data once when we first find the existing coupon
		if (existingCoupon && isEditing && !hasLoadedCoupon && !isLoading) {
			// Determine which languages are enabled based on existing translations
			const enabledLangs: SupportedLanguage[] = ['en'];

			setFormData({
				couponTitle: existingCoupon.couponTitle,
				couponDescription: existingCoupon.couponDescription,
				couponType: existingCoupon.couponType,
				// Convert cents to dollars for display in form
				// For percentage type, value is already a percentage (not cents)
				couponValue: existingCoupon.couponType === CouponTypeEnum.percentage
					? existingCoupon.couponValue
					: centsToDollars(existingCoupon.couponValue),
				// Load text value for free_item/bogo types
				couponTextValue: '',
				// Convert minimum amount from cents to dollars if it exists
				couponMinimumAmount: existingCoupon.couponMinimumAmount !== null
					? centsToDollars(existingCoupon.couponMinimumAmount)
					: undefined,
				couponCommissionPercentage: 0,
				// Load expiration date - convert to YYYY-MM-DD format for date input
				couponExpiresAt: toDateInputValue(existingCoupon.expires_at),
				// Multi-language fields (Story 4.2)
				titleFr: '',
				descriptionFr: '',
				titleZh: '',
				descriptionZh: '',
				enabledLanguages: enabledLangs,
				// Background image (Story 4.3)
				backgroundImageUrl: null,
			});
			setHasLoadedCoupon(true);
		}
	}, [existingCoupon, isEditing, hasLoadedCoupon, isLoading]);

	// Reset the flag when the ID changes (navigating to a different coupon)
	useEffect(() => {
		setHasLoadedCoupon(false);
	}, [id]);

	// Helper to check if coupon type requires text value
	const requiresTextValue = (type: string) => type === CouponTypeEnum.free_item || type === CouponTypeEnum.bogo;
	const requiresNumericValue = (type: string) => type === CouponTypeEnum.percentage || type === CouponTypeEnum.fixed_amount;
	const hasText = (value: string | undefined): boolean => (value?.trim().length ?? 0) > 0;
	const normalizeOptionalText = (value: string | null | undefined): string | null => {
		const trimmed = value?.trim() ?? '';
		return trimmed.length > 0 ? trimmed : null;
	};
	const isFiniteNumber = (value: number | undefined): value is number =>
		typeof value === 'number' && Number.isFinite(value);

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
		const { name, value, type } = e.target;

		if (type === 'checkbox') {
			const checked = (e.target as HTMLInputElement).checked;
			setFormData(prev => ({ ...prev, [name]: checked }));
		} else if (name === 'couponValue' || name === 'couponMinimumAmount' || name === 'couponCommissionPercentage') {
			const numValue = value === '' ? 0 : Number(value);
			setFormData(prev => ({ ...prev, [name]: numValue }));
		} else if (name === 'couponType') {
			// When coupon type changes, clear the opposite value field
			setFormData(prev => ({
				...prev,
				couponType: value,
				// Clear couponValue when switching to text-based type
				couponValue: requiresTextValue(value) ? 0 : prev.couponValue,
				// Clear couponTextValue when switching to numeric type
				couponTextValue: requiresNumericValue(value) ? '' : prev.couponTextValue,
			}));
		} else {
			setFormData(prev => ({ ...prev, [name]: value }));
		}

		// Clear specific error when user types
		if (errors[name as keyof CouponFormErrors]) {
			setErrors(prev => ({ ...prev, [name]: undefined }));
		}
	};

	// Story 4.5: Handle proceeding to Step 2 (Distribute)
	const handleProceedToDistribute = () => {
		if (validateForm()) {
			setCurrentStep(2);
		}
	};

	// Story 4.5: Handle going back to Step 1 (Create)
	const handleBackToCreate = () => {
		setCurrentStep(1);
	};

	const validateForm = (): boolean => {
		const newErrors: CouponFormErrors = {};
		const title = formData.couponTitle.trim();
		const description = formData.couponDescription.trim();
		const couponExpiresAt = formData.couponExpiresAt ?? '';

		// Basic validation (since we're not importing couponValidation)
			if (title.length === 0) {
				newErrors.couponTitle = t('merchant.coupon.validation.titleRequired');
			} else if (title.length < 2 || title.length > 100) {
				newErrors.couponTitle = t('merchant.coupon.validation.titleLength');
			}

			if (description.length === 0) {
				newErrors.couponDescription = t('merchant.coupon.validation.descriptionRequired');
			} else if (description.length < 2 || description.length > 500) {
				newErrors.couponDescription = t('merchant.coupon.validation.descriptionLength');
			}

		// Type-specific validation: numeric value for percentage/fixed, text value for free_item/bogo
		if (requiresNumericValue(formData.couponType)) {
			if (!Number.isFinite(formData.couponValue) || formData.couponValue <= 0) {
				newErrors.couponValue = t('merchant.coupon.validation.valueMustBePositive');
			}
			if (formData.couponType === CouponTypeEnum.percentage && formData.couponValue > 100) {
				newErrors.couponValue = t('merchant.coupon.validation.percentageMax');
			}
		} else if (requiresTextValue(formData.couponType)) {
			if (!hasText(formData.couponTextValue)) {
				newErrors.couponTextValue = t('merchant.coupon.validation.textRequired');
			}
		}

			// Expiration date validation
			if (couponExpiresAt.length === 0) {
				newErrors.couponExpiresAt = t('merchant.coupon.validation.expirationRequired');
			} else {
				const expirationDate = parseExpirationDateInput(couponExpiresAt);
				if (
					expirationDate === null ||
					TimestampService.getMinutesFromNow(expirationDate) <= 0
				) {
					newErrors.couponExpiresAt = t('merchant.coupon.validation.expirationFuture');
				}
			}

		if (
			formData.couponMinimumAmount !== undefined &&
			(!Number.isFinite(formData.couponMinimumAmount) || formData.couponMinimumAmount < 0)
		) {
			newErrors.couponMinimumAmount = t('merchant.coupon.validation.minimumNonNegative');
		}

		if (
			formData.couponCommissionPercentage !== undefined &&
			(
				!Number.isFinite(formData.couponCommissionPercentage) ||
				formData.couponCommissionPercentage < 0 ||
				formData.couponCommissionPercentage > 100
			)
		) {
			newErrors.couponCommissionPercentage = t('merchant.coupon.validation.commissionRange');
		}

		// Multi-language validation (Story 4.2 - AC #5)
		// Validate French if enabled
		if (formData.enabledLanguages.includes('fr')) {
				const titleFr = formData.titleFr?.trim() ?? '';
				const descriptionFr = formData.descriptionFr?.trim() ?? '';
				if (titleFr.length === 0) {
					newErrors.titleFr = t('merchant.coupon.validation.titleRequired');
				} else if (titleFr.length < 2 || titleFr.length > 200) {
					newErrors.titleFr = t('merchant.coupon.validation.titleLength');
				}
				if (descriptionFr.length === 0) {
					newErrors.descriptionFr = t('merchant.coupon.validation.descriptionRequired');
				} else if (descriptionFr.length < 2 || descriptionFr.length > 500) {
					newErrors.descriptionFr = t('merchant.coupon.validation.descriptionLength');
				}
		}
		// Validate Chinese if enabled
		if (formData.enabledLanguages.includes('zh')) {
				const titleZh = formData.titleZh?.trim() ?? '';
				const descriptionZh = formData.descriptionZh?.trim() ?? '';
				if (titleZh.length === 0) {
					newErrors.titleZh = t('merchant.coupon.validation.titleRequired');
				} else if (titleZh.length < 2 || titleZh.length > 200) {
					newErrors.titleZh = t('merchant.coupon.validation.titleLength');
				}
				if (descriptionZh.length === 0) {
					newErrors.descriptionZh = t('merchant.coupon.validation.descriptionRequired');
				} else if (descriptionZh.length < 2 || descriptionZh.length > 500) {
					newErrors.descriptionZh = t('merchant.coupon.validation.descriptionLength');
				}
			}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	// Generate discount preview
	const getDiscountPreview = () => {
		if (formData.couponType === CouponTypeEnum.percentage) {
			if (!Number.isFinite(formData.couponValue) || formData.couponValue <= 0) return '';
			return `${formData.couponValue}% OFF`;
		} else if (formData.couponType === CouponTypeEnum.fixed_amount) {
			if (!Number.isFinite(formData.couponValue) || formData.couponValue <= 0) return '';
			return `$${formData.couponValue.toFixed(2)} OFF`;
		} else if (formData.couponType === CouponTypeEnum.free_item) {
			return normalizeOptionalText(formData.couponTextValue) ?? t('merchant.coupon.form.textValuePlaceholder');
		} else if (formData.couponType === CouponTypeEnum.bogo) {
			return normalizeOptionalText(formData.couponTextValue) ?? t('merchant.coupon.form.textValuePlaceholder');
		}
		return '';
	};

	// Story 4.6: Open confirmation modal before final submit
	const handleOpenConfirmation = () => {
		if (!memberSelection.isValid) {
			return;
		}
		setShowConfirmModal(true);
	};

	// Story 4.6: Close confirmation modal
	const handleCloseConfirmation = () => {
		setShowConfirmModal(false);
	};

	const submitCoupon = async () => {
		// Prevent submission when in edit mode
		if (isEditing) {
			return;
		}

		setSuccessMessage(null);
		setSubmitError(null);

		if (!validateForm()) {
			return;
		}

		if (selectedMerchantId === null) {
			setErrors({ couponTitle: t('merchant.coupon.validation.noMerchantSelected') });
			return;
		}

		// MERGED: Create coupon AND distribute in a SINGLE API call
		// distributions field is now REQUIRED - coupon is created+distributed atomically
		// Determine value based on coupon type
		if (requiresNumericValue(formData.couponType) && !Number.isFinite(formData.couponValue)) {
			setSubmitError(t('merchant.coupon.validation.valueMustBePositive'));
			return;
		}
		const couponValue = requiresNumericValue(formData.couponType)
			? formData.couponType === CouponTypeEnum.percentage
				? formData.couponValue
				: dollarsToCents(formData.couponValue)
			: null;
		const expiresAtInput = formData.couponExpiresAt ?? '';
		if (expiresAtInput.length === 0) {
			setSubmitError(t('merchant.coupon.validation.expirationRequired'));
			return;
		}
		const parsedExpiresAt = parseExpirationDateInput(expiresAtInput);
		if (parsedExpiresAt === null) {
			setSubmitError(t('merchant.coupon.validation.expirationFuture'));
			return;
		}

		// Map form fields to API schema field names (snake_case)
			const createData = {
				title: formData.couponTitle,
			description: formData.couponDescription,
			coupon_type: formData.couponType as CouponType,
			coupon_value: couponValue,
			text_value: requiresTextValue(formData.couponType) ? normalizeOptionalText(formData.couponTextValue) : null,
			minimum_amount: isFiniteNumber(formData.couponMinimumAmount)
				? dollarsToCents(formData.couponMinimumAmount)
				: null,
			commission_basis_points: isFiniteNumber(formData.couponCommissionPercentage)
				? BasisPointsService.fromPercentage(formData.couponCommissionPercentage)
				: 0,
				expires_at: TimestampService.toIsoString(parsedExpiresAt),
			starts_at: null,
			max_redemptions: null,
			background_image_url: normalizeOptionalText(formData.backgroundImageUrl),
			title_fr: formData.enabledLanguages.includes('fr') ? normalizeOptionalText(formData.titleFr) : null,
			description_fr: formData.enabledLanguages.includes('fr') ? normalizeOptionalText(formData.descriptionFr) : null,
			title_zh: formData.enabledLanguages.includes('zh') ? normalizeOptionalText(formData.titleZh) : null,
			description_zh: formData.enabledLanguages.includes('zh') ? normalizeOptionalText(formData.descriptionZh) : null,
			distributions: memberSelection.getDistributionData(),
		};

		try {
			const result = await couponActions.createCoupon(selectedMerchantId, createData);

			setShowConfirmModal(false);
			setDistributionSuccess({
				couponTitle: formData.couponTitle,
				totalCoupons: result.total_instances_created,
				memberCount: result.member_count,
			});
		} catch {
			setSubmitError(t('merchant.coupon.saveError'));
			setShowConfirmModal(false);
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		void submitCoupon();
	};

	const isSubmitting = couponActions.isLoading.createCoupon;

	// Loading state
	if (isLoading) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='flex items-center justify-center h-64'>
					<div className='inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-gray-500 bg-white'>
						<div className='animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500'>
							<svg className='w-5 h-5' fill='none' viewBox='0 0 24 24'>
								<circle
									className='opacity-25'
									cx='12'
									cy='12'
									r='10'
									stroke='currentColor'
									strokeWidth='4'
								/>
								<path
									className='opacity-75'
									fill='currentColor'
									d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
								/>
							</svg>
						</div>
						{t('common.loading')}
					</div>
				</div>
			</div>
		);
	}

	// No merchant selected
	if (selectedMerchantId === null) {
		return (
			<div className='flex-1 overflow-auto p-6'>
				<div className='bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
					<p className='text-yellow-600'>{t('merchant.coupon.noMerchantSelected')}</p>
				</div>
			</div>
		);
	}

	// Story 4.6 AC6: Success Screen after distribution
	if (distributionSuccess) {
		return (
			<div className='flex-1 overflow-auto p-4 sm:p-6'>
				<div className='max-w-md mx-auto'>
					<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8 text-center'>
						{/* Success Icon */}
						<div className='mb-6'>
							<div className='w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center'>
								<CheckCircle className='w-10 h-10 text-green-600' />
							</div>
						</div>

						{/* Title */}
						<h1 className='text-xl sm:text-2xl font-bold text-gray-900 mb-4'>
							{t('merchant.coupon.distribute.successScreen.title')}
						</h1>

						{/* Coupon Details */}
						<div className='mb-6 p-4 bg-gray-50 rounded-lg'>
							<p className='text-sm text-gray-500 mb-1'>
								{t('merchant.coupon.distribute.successScreen.couponLabel')}
							</p>
							<p className='text-lg font-semibold text-gray-900'>
								{distributionSuccess.couponTitle}
							</p>
							<p className='text-sm text-blue-600 mt-2'>
								{t('merchant.coupon.distribute.successScreen.totalDistributed', {
									total: distributionSuccess.totalCoupons,
									members: distributionSuccess.memberCount,
								})}
							</p>
						</div>

						{/* Notification Note */}
						<p className='text-sm text-gray-500 mb-6'>
							{t('merchant.coupon.distribute.successScreen.notificationNote')}
						</p>

						{/* Action Buttons */}
						<div className='space-y-3'>
								<button
									onClick={() => {
										void navigate('/merchant/coupon-list');
									}}
									className='w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium'
								>
								{t('merchant.coupon.distribute.successScreen.viewCoupons')}
							</button>
							<button
								onClick={() => {
									setDistributionSuccess(null);
									setCurrentStep(1);
									setFormData({
										couponTitle: '',
										couponDescription: '',
										couponType: CouponTypeEnum.percentage,
										couponValue: 0,
										couponTextValue: '',
										couponCommissionPercentage: 0,
										couponExpiresAt: '',
										titleFr: '',
										descriptionFr: '',
										titleZh: '',
										descriptionZh: '',
										enabledLanguages: ['en'],
										backgroundImageUrl: null,
									});
									memberSelection.resetSelection();
								}}
								className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium'
							>
								{t('merchant.coupon.distribute.successScreen.createAnother')}
							</button>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='flex-1 overflow-auto p-4 sm:p-6'>
			{/* Header */}
				<div className='mb-6 sm:mb-8'>
					<button
						onClick={() => {
							if (currentStep === 2) {
								handleBackToCreate();
								return;
							}
							void navigate('/merchant/coupon-list');
						}}
						className='inline-flex items-center px-3 py-2 mb-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors'
					>
					<ArrowLeft className='w-4 h-4 mr-2' />
					{currentStep === 2 ? t('merchant.coupon.distribute.backToEdit') : t('merchant.coupon.backToCoupons')}
				</button>

				{/* Step Indicator - Story 4.5 (only show when creating, not editing) */}
				{!isEditing && (
					<div className='flex items-center gap-2 mb-4'>
						<div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
							currentStep === 1 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
						}`}>
							<span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${
								currentStep === 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
							}`}>1</span>
							{t('merchant.coupon.createTitle')}
						</div>
						<ArrowRight className='w-4 h-4 text-gray-400' />
						<div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
							currentStep === 2 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
						}`}>
							<span className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${
								currentStep === 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
							}`}>2</span>
							{t('merchant.coupon.distribute.title')}
						</div>
					</div>
				)}

				<h1 className='text-xl sm:text-2xl font-bold text-gray-900 mb-2'>
					{isEditing
						? t('merchant.coupon.viewTitle')
						: currentStep === 2
							? t('merchant.coupon.distribute.title')
							: t('merchant.coupon.createTitle')}
				</h1>
				<p className='text-gray-500 text-sm sm:text-base'>
					{isEditing
						? t('merchant.coupon.viewSubtitle')
						: currentStep === 2
							? t('merchant.coupon.distribute.subtitle')
							: t('merchant.coupon.createSubtitle')}
				</p>
			</div>

			{/* Success Message */}
			{successMessage && (
				<div className='mb-6 bg-green-50 border border-green-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<CheckCircle className='h-5 w-5 text-green-600 mr-2' />
						<p className='text-sm text-green-600'>{successMessage}</p>
					</div>
				</div>
			)}

			{/* Error Message */}
			{submitError && (
				<div className='mb-6 bg-red-50 border border-red-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<AlertCircle className='h-5 w-5 text-red-600 mr-2' />
						<p className='text-sm text-red-600'>{submitError}</p>
					</div>
				</div>
			)}

			{/* Read-only Notice */}
			{isEditing && (
				<div className='mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<AlertCircle className='h-5 w-5 text-yellow-600 mr-2' />
							<p className='text-sm text-yellow-700'>
								{t('merchant.coupon.readOnlyNotice')}
							</p>
					</div>
				</div>
			)}

			{/* Step 1: Form and Preview */}
			{currentStep === 1 && (
			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8'>
				{/* Form */}
				<div className='order-2 lg:order-1'>
					<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6'>
						<form onSubmit={handleSubmit} className='space-y-4 sm:space-y-6'>
							{/* Language Tabs - Story 4.2 AC #1, #2, #3 */}
								<div>
									<h3 className='text-sm font-medium text-gray-700 mb-3'>
										{t('merchant.coupon.form.contentSection')}
									</h3>
								<LanguageTabs
									enabledLanguages={formData.enabledLanguages}
									activeLanguage={activeLanguageTab}
									onLanguageSelect={setActiveLanguageTab}
									onLanguageAdd={handleAddLanguage}
									onLanguageRemove={handleRemoveLanguage}
									disabled={isEditing}
								/>
							</div>

							{/* English Content - shown when EN tab active */}
							{activeLanguageTab === 'en' && (
								<>
									{/* Title */}
									<div>
										<label htmlFor='couponTitle' className='block text-sm font-medium text-gray-700 mb-2'>
											{requiredLabel('merchant.coupon.form.titleLabel')}
										</label>
										<input
											type='text'
											id='couponTitle'
											name='couponTitle'
											value={formData.couponTitle}
											onChange={handleInputChange}
											disabled={isEditing}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.couponTitle ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
											placeholder={t('merchant.coupon.form.titlePlaceholder')}
										/>
										{errors.couponTitle && (
											<p className='mt-1 text-sm text-red-600'>{errors.couponTitle}</p>
										)}
									</div>

									{/* Description */}
									<div>
										<label
											htmlFor='couponDescription'
											className='block text-sm font-medium text-gray-700 mb-2'
										>
											{requiredLabel('merchant.coupon.form.descriptionLabel')}
										</label>
										<textarea
											id='couponDescription'
											name='couponDescription'
											value={formData.couponDescription}
											onChange={handleInputChange}
											disabled={isEditing}
											rows={3}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.couponDescription ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
											placeholder={t('merchant.coupon.form.descriptionPlaceholder')}
										/>
										{errors.couponDescription && (
											<p className='mt-1 text-sm text-red-600'>{errors.couponDescription}</p>
										)}
									</div>
								</>
							)}

							{/* French Content - shown when FR tab active */}
							{activeLanguageTab === 'fr' && formData.enabledLanguages.includes('fr') && (
								<>
										<div>
											<label htmlFor='titleFr' className='block text-sm font-medium text-gray-700 mb-2'>
												{requiredLabelWithLanguage('merchant.coupon.form.titleLabel', 'merchant.coupon.form.languageTabFrench')}
											</label>
										<input
											type='text'
											id='titleFr'
											name='titleFr'
											value={formData.titleFr || ''}
											onChange={handleInputChange}
											disabled={isEditing}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.titleFr ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
												placeholder={t('merchant.coupon.form.titlePlaceholderFr')}
										/>
										{errors.titleFr && (
											<p className='mt-1 text-sm text-red-600'>{errors.titleFr}</p>
										)}
									</div>

									<div>
											<label
												htmlFor='descriptionFr'
												className='block text-sm font-medium text-gray-700 mb-2'
											>
												{requiredLabelWithLanguage('merchant.coupon.form.descriptionLabel', 'merchant.coupon.form.languageTabFrench')}
											</label>
										<textarea
											id='descriptionFr'
											name='descriptionFr'
											value={formData.descriptionFr || ''}
											onChange={handleInputChange}
											disabled={isEditing}
											rows={3}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.descriptionFr ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
												placeholder={t('merchant.coupon.form.descriptionPlaceholderFr')}
										/>
										{errors.descriptionFr && (
											<p className='mt-1 text-sm text-red-600'>{errors.descriptionFr}</p>
										)}
									</div>
								</>
							)}

							{/* Chinese Content - shown when ZH tab active */}
							{activeLanguageTab === 'zh' && formData.enabledLanguages.includes('zh') && (
								<>
										<div>
											<label htmlFor='titleZh' className='block text-sm font-medium text-gray-700 mb-2'>
												{requiredLabelWithLanguage('merchant.coupon.form.titleLabel', 'merchant.coupon.form.languageTabChinese')}
											</label>
										<input
											type='text'
											id='titleZh'
											name='titleZh'
											value={formData.titleZh || ''}
											onChange={handleInputChange}
											disabled={isEditing}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.titleZh ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
												placeholder={t('merchant.coupon.form.titlePlaceholderZh')}
										/>
										{errors.titleZh && (
											<p className='mt-1 text-sm text-red-600'>{errors.titleZh}</p>
										)}
									</div>

									<div>
											<label
												htmlFor='descriptionZh'
												className='block text-sm font-medium text-gray-700 mb-2'
											>
												{requiredLabelWithLanguage('merchant.coupon.form.descriptionLabel', 'merchant.coupon.form.languageTabChinese')}
											</label>
										<textarea
											id='descriptionZh'
											name='descriptionZh'
											value={formData.descriptionZh || ''}
											onChange={handleInputChange}
											disabled={isEditing}
											rows={3}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												errors.descriptionZh ? 'border-red-300' : 'border-gray-300'
											} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
												placeholder={t('merchant.coupon.form.descriptionPlaceholderZh')}
										/>
										{errors.descriptionZh && (
											<p className='mt-1 text-sm text-red-600'>{errors.descriptionZh}</p>
										)}
									</div>
								</>
							)}

							{/* Discount Type */}
							<div>
								<label
									htmlFor='couponType'
									className='block text-sm font-medium text-gray-700 mb-2'
								>
									{requiredLabel('merchant.coupon.form.discountTypeLabel')}
								</label>
								<select
									id='couponType'
									name='couponType'
									value={formData.couponType}
									onChange={handleInputChange}
									disabled={isEditing}
									className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
								>
									<option value={CouponTypeEnum.percentage}>
										{t('merchant.coupon.form.discountTypePercentage')}
									</option>
									<option value={CouponTypeEnum.fixed_amount}>
										{t('merchant.coupon.form.discountTypeFixed')}
									</option>
									<option value={CouponTypeEnum.free_item}>
										{t('merchant.coupon.form.discountTypeFreeItem')}
									</option>
									<option value={CouponTypeEnum.bogo}>
										{t('merchant.coupon.form.discountTypeBogo')}
									</option>
								</select>
							</div>

							{/* Numeric Value - shown for percentage/fixed types */}
							{requiresNumericValue(formData.couponType) && (
								<div>
									<label
										htmlFor='couponValue'
										className='block text-sm font-medium text-gray-700 mb-2'
									>
										{requiredLabel('merchant.coupon.form.discountValueLabel')}
									</label>
									<div className='relative'>
										{formData.couponType === CouponTypeEnum.percentage && (
											<span className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500'>
												%
											</span>
										)}
										{formData.couponType === CouponTypeEnum.fixed_amount && (
											<span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500'>
												{currencySymbol}
											</span>
										)}
										<input
											type='number'
											id='couponValue'
											name='couponValue'
											value={formData.couponValue || ''}
											onChange={handleInputChange}
											disabled={isEditing}
											min='0'
											max={formData.couponType === CouponTypeEnum.percentage ? '100' : undefined}
											step={formData.couponType === CouponTypeEnum.percentage ? '1' : '0.01'}
											className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
												formData.couponType === CouponTypeEnum.fixed_amount ? 'pl-8' : 'pr-8'
											} ${errors.couponValue ? 'border-red-300' : 'border-gray-300'} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
											placeholder={
												formData.couponType === CouponTypeEnum.percentage ? '20' : '5.00'
											}
										/>
									</div>
									{errors.couponValue && (
										<p className='mt-1 text-sm text-red-600'>{errors.couponValue}</p>
									)}
								</div>
							)}

							{/* Text Value - shown for free_item/bogo types */}
							{requiresTextValue(formData.couponType) && (
								<div>
									<label
										htmlFor='couponTextValue'
										className='block text-sm font-medium text-gray-700 mb-2'
									>
										{requiredLabel('merchant.coupon.form.textValueLabel')}
									</label>
									<textarea
										id='couponTextValue'
										name='couponTextValue'
										value={formData.couponTextValue || ''}
										onChange={handleInputChange}
										disabled={isEditing}
										rows={2}
										className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
											errors.couponTextValue ? 'border-red-300' : 'border-gray-300'
										} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
										placeholder={t('merchant.coupon.form.textValuePlaceholder')}
									/>
									{errors.couponTextValue && (
										<p className='mt-1 text-sm text-red-600'>{errors.couponTextValue}</p>
									)}
								</div>
							)}

							{/* Expiration Date */}
							<div>
								<label
									htmlFor='couponExpiresAt'
									className='block text-sm font-medium text-gray-700 mb-2'
								>
									{requiredLabel('merchant.coupon.form.expirationLabel')}
								</label>
								<input
									type='date'
									id='couponExpiresAt'
									name='couponExpiresAt'
									value={formData.couponExpiresAt || ''}
									onChange={handleInputChange}
									disabled={isEditing}
									aria-describedby='couponExpiresAt-hint couponExpiresAt-error'
									aria-invalid={!!errors.couponExpiresAt}
									className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
										errors.couponExpiresAt ? 'border-red-300' : 'border-gray-300'
									} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
								/>
								{errors.couponExpiresAt && (
									<p id='couponExpiresAt-error' className='mt-1 text-sm text-red-600' role='alert'>{errors.couponExpiresAt}</p>
								)}
								<p id='couponExpiresAt-hint' className='mt-1 text-sm text-gray-500'>
									{t('merchant.coupon.form.expirationHint')}
								</p>
							</div>

							{/* Minimum Amount */}
							<div>
								<label
									htmlFor='couponMinimumAmount'
									className='block text-sm font-medium text-gray-700 mb-2'
								>
									{t('merchant.coupon.form.minimumAmountLabel')}
								</label>
								<div className='relative'>
									<span className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500'>
										{currencySymbol}
									</span>
									<input
										type='number'
										id='couponMinimumAmount'
										name='couponMinimumAmount'
										value={formData.couponMinimumAmount || ''}
										onChange={handleInputChange}
										disabled={isEditing}
										min='0'
										step='0.01'
										className={`w-full pl-8 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
											errors.couponMinimumAmount ? 'border-red-300' : 'border-gray-300'
										} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
										placeholder={t('merchant.coupon.form.minimumAmountPlaceholder')}
									/>
								</div>
								{errors.couponMinimumAmount && (
									<p className='mt-1 text-sm text-red-600'>{errors.couponMinimumAmount}</p>
								)}
								<p className='mt-1 text-sm text-gray-500'>
									{t('merchant.coupon.form.minimumAmountHint')}
								</p>
							</div>

							{/* Commission Rate */}
							<div>
								<label
									htmlFor='couponCommissionPercentage'
									className='block text-sm font-medium text-gray-700 mb-2'
								>
									{t('merchant.coupon.form.commissionRateLabel')}
								</label>
								<div className='relative'>
									<span className='absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500'>
										%
									</span>
									<input
										type='number'
										id='couponCommissionPercentage'
										name='couponCommissionPercentage'
										value={formData.couponCommissionPercentage || ''}
										onChange={handleInputChange}
										disabled={isEditing}
										min='0'
										max='100'
										step='0.01'
										className={`w-full pr-8 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
											errors.couponCommissionPercentage ? 'border-red-300' : 'border-gray-300'
										} ${isEditing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
										placeholder={t('merchant.coupon.form.commissionRatePlaceholder')}
									/>
								</div>
								{errors.couponCommissionPercentage && (
									<p className='mt-1 text-sm text-red-600'>{errors.couponCommissionPercentage}</p>
								)}
								<p className='mt-1 text-sm text-gray-500'>
									{t('merchant.coupon.form.commissionRateHint')}
								</p>
							</div>

							{/* Background Image Selection - Story 4.3 */}
								<div>
									<h3 className='text-sm font-medium text-gray-700 mb-2'>
										{t('merchant.coupon.form.backgroundSection')}
									</h3>
									<p className='text-xs text-gray-500 mb-3'>
										{t('merchant.coupon.form.backgroundSectionHint')}
									</p>
								<BackgroundGallery
									value={formData.backgroundImageUrl || null}
									onChange={(url) => setFormData(prev => ({ ...prev, backgroundImageUrl: url }))}
									disabled={isEditing}
								/>
							</div>

							{/* Mobile Action Buttons */}
							<div className='block lg:hidden pt-4 border-t border-gray-200 space-y-3'>
								{!isEditing && (
										<button
											type='button'
											onClick={handleProceedToDistribute}
											className='w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base'
										>
											{t('merchant.coupon.distribute.nextSelectMembers')}
											<ArrowRight className='w-4 h-4 ml-2' />
										</button>
								)}

									<button
										type='button'
										onClick={() => {
											void navigate('/merchant/coupon-list');
										}}
										className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm sm:text-base'
									>
									{isEditing ? t('common.back') : t('common.cancel')}
								</button>
							</div>
						</form>
					</div>
				</div>

				{/* Preview */}
				<div className='order-1 lg:order-2'>
					<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 lg:sticky lg:top-6'>
						<h3 className='text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center'>
							<Eye className='w-4 h-4 sm:w-5 sm:h-5 mr-2' />
							{t('merchant.coupon.preview')}
						</h3>

						{/* Multi-language Preview Cards - Story 4.2 AC #4 */}
						<div className='space-y-4'>
							{/* English Preview Card - with background image support (Story 4.3 AC#4) */}
							<div
								className='border-2 border-dashed border-gray-300 rounded-lg p-4 overflow-hidden'
								style={{
									backgroundImage: formData.backgroundImageUrl ? `url(${formData.backgroundImageUrl})` : undefined,
									backgroundSize: 'cover',
									backgroundPosition: 'center',
									backgroundColor: formData.backgroundImageUrl ? undefined : 'rgb(249 250 251)', // bg-gray-50 equivalent
								}}
							>
								<div className='text-xs font-medium text-blue-600 mb-2 uppercase tracking-wide'>
									{t('merchant.coupon.form.languageTabEnglish')}
								</div>
								<div className='text-center'>
									{/* Coupon Title - with text shadow for readability over image */}
									<h4
										className='text-lg sm:text-xl font-bold mb-2'
										style={{
											color: formData.backgroundImageUrl ? 'white' : 'rgb(17 24 39)', // white or gray-900
											textShadow: formData.backgroundImageUrl ? '0 1px 3px rgba(0,0,0,0.7)' : undefined,
										}}
									>
										{formData.couponTitle || t('merchant.coupon.form.titlePlaceholder')}
									</h4>

									{/* Coupon Description */}
									<p
										className='text-sm mb-3'
										style={{
											color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.9)' : 'rgb(75 85 99)', // white-ish or gray-600
											textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
										}}
									>
										{formData.couponDescription || t('merchant.coupon.form.descriptionPlaceholder')}
									</p>

									{/* Discount Value */}
									{(formData.couponValue > 0 || requiresTextValue(formData.couponType)) && (
										<div
											className='text-2xl sm:text-3xl font-bold mb-3'
											style={{
												color: formData.backgroundImageUrl ? 'white' : 'rgb(37 99 235)', // white or blue-600
												textShadow: formData.backgroundImageUrl ? '0 2px 4px rgba(0,0,0,0.5)' : undefined,
											}}
										>
											{getDiscountPreview()}
										</div>
									)}

									{/* Expiration Date */}
									{formData.couponExpiresAt && (
										<div
											className='text-xs mb-2'
											style={{
												color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.8)' : 'rgb(107 114 128)', // white-ish or gray-500
												textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
											}}
										>
											{t('merchant.coupon.previewExpires', {
												date: formatExpiryForPreview(formData.couponExpiresAt)
											})}
										</div>
									)}

									{/* Minimum Spend */}
									<div
										className='text-xs'
										style={{
											color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.8)' : 'rgb(107 114 128)', // white-ish or gray-500
											textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
										}}
									>
										{formData.couponMinimumAmount
											? `${t('merchant.coupon.minimumSpend')}: $${formData.couponMinimumAmount.toFixed(2)}`
											: t('merchant.coupon.noMinimum')}
									</div>
								</div>
							</div>

							{/* French Preview Card - shown if FR enabled, with background image support */}
							{formData.enabledLanguages.includes('fr') && (
								<div
									className='border-2 border-dashed border-blue-200 rounded-lg p-4 overflow-hidden'
									style={{
										backgroundImage: formData.backgroundImageUrl ? `url(${formData.backgroundImageUrl})` : undefined,
										backgroundSize: 'cover',
										backgroundPosition: 'center',
										backgroundColor: formData.backgroundImageUrl ? undefined : 'rgb(239 246 255)', // bg-blue-50 equivalent
									}}
								>
									<div className='text-xs font-medium text-blue-600 mb-2 uppercase tracking-wide'>
										{t('merchant.coupon.form.languageTabFrench')}
									</div>
									<div className='text-center'>
										<h4
											className='text-lg sm:text-xl font-bold mb-2'
											style={{
												color: formData.backgroundImageUrl ? 'white' : 'rgb(17 24 39)',
												textShadow: formData.backgroundImageUrl ? '0 1px 3px rgba(0,0,0,0.7)' : undefined,
											}}
										>
											{formData.titleFr || t('merchant.coupon.form.titlePlaceholderFr')}
										</h4>
										<p
											className='text-sm mb-3'
											style={{
												color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.9)' : 'rgb(75 85 99)',
												textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
											}}
										>
											{formData.descriptionFr || t('merchant.coupon.form.descriptionPlaceholderFr')}
										</p>
										{(formData.couponValue > 0 || requiresTextValue(formData.couponType)) && (
											<div
												className='text-2xl sm:text-3xl font-bold mb-3'
												style={{
													color: formData.backgroundImageUrl ? 'white' : 'rgb(37 99 235)',
													textShadow: formData.backgroundImageUrl ? '0 2px 4px rgba(0,0,0,0.5)' : undefined,
												}}
											>
												{getDiscountPreview()}
											</div>
										)}
										{/* Expiration Date */}
										{formData.couponExpiresAt && (
											<div
												className='text-xs mb-2'
												style={{
													color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.8)' : 'rgb(107 114 128)',
													textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
												}}
											>
												{t('merchant.coupon.previewExpires', {
													date: formatExpiryForPreview(formData.couponExpiresAt)
												})}
											</div>
										)}
									</div>
								</div>
							)}

							{/* Chinese Preview Card - shown if ZH enabled, with background image support */}
							{formData.enabledLanguages.includes('zh') && (
								<div
									className='border-2 border-dashed border-green-200 rounded-lg p-4 overflow-hidden'
									style={{
										backgroundImage: formData.backgroundImageUrl ? `url(${formData.backgroundImageUrl})` : undefined,
										backgroundSize: 'cover',
										backgroundPosition: 'center',
										backgroundColor: formData.backgroundImageUrl ? undefined : 'rgb(240 253 244)', // bg-green-50 equivalent
									}}
								>
									<div className='text-xs font-medium text-green-600 mb-2 uppercase tracking-wide'>
										{t('merchant.coupon.form.languageTabChinese')}
									</div>
									<div className='text-center'>
										<h4
											className='text-lg sm:text-xl font-bold mb-2'
											style={{
												color: formData.backgroundImageUrl ? 'white' : 'rgb(17 24 39)',
												textShadow: formData.backgroundImageUrl ? '0 1px 3px rgba(0,0,0,0.7)' : undefined,
											}}
										>
											{formData.titleZh || t('merchant.coupon.form.titlePlaceholderZh')}
										</h4>
										<p
											className='text-sm mb-3'
											style={{
												color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.9)' : 'rgb(75 85 99)',
												textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
											}}
										>
											{formData.descriptionZh || t('merchant.coupon.form.descriptionPlaceholderZh')}
										</p>
										{(formData.couponValue > 0 || requiresTextValue(formData.couponType)) && (
											<div
												className='text-2xl sm:text-3xl font-bold mb-3'
												style={{
													color: formData.backgroundImageUrl ? 'white' : 'rgb(22 163 74)',
													textShadow: formData.backgroundImageUrl ? '0 2px 4px rgba(0,0,0,0.5)' : undefined,
												}}
											>
												{getDiscountPreview()}
											</div>
										)}
										{/* Expiration Date */}
										{formData.couponExpiresAt && (
											<div
												className='text-xs mb-2'
												style={{
													color: formData.backgroundImageUrl ? 'rgba(255,255,255,0.8)' : 'rgb(107 114 128)',
													textShadow: formData.backgroundImageUrl ? '0 1px 2px rgba(0,0,0,0.5)' : undefined,
												}}
											>
												{t('merchant.coupon.previewExpires', {
													date: formatExpiryForPreview(formData.couponExpiresAt)
												})}
											</div>
										)}
									</div>
								</div>
							)}
						</div>

						{/* Desktop Action Buttons */}
						<div className='hidden lg:block mt-6 space-y-3'>
							{!isEditing && (
								<button
									type='button'
									onClick={handleProceedToDistribute}
									className='w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors'
								>
									{t('merchant.coupon.distribute.nextSelectMembers')}
									<ArrowRight className='w-4 h-4 ml-2' />
								</button>
							)}

								<button
									type='button'
									onClick={() => {
										void navigate('/merchant/coupon-list');
									}}
									className='w-full px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium'
								>
								{isEditing ? t('common.back') : t('common.cancel')}
							</button>
						</div>
					</div>
				</div>
			</div>
			)}

			{/* Step 2: Member Selection (Story 4.5) */}
			{currentStep === 2 && !isEditing && (
				<div className='max-w-2xl mx-auto'>
					<div className='bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6'>
							{/* Member List or Empty State */}
						{!memberSelection.isLoading && memberSelection.members.length === 0 ? (
							<div className='flex flex-col items-center justify-center py-12'>
								<Users className='w-12 h-12 text-gray-300 mb-4' />
								<p className='text-gray-500 mb-2'>{t('merchant.coupon.distribute.noMembers')}</p>
								<Link
									to='/merchant/members'
									className='text-blue-600 hover:text-blue-700 font-medium'
								>
									{t('merchant.coupon.distribute.goToMembers')}
								</Link>
							</div>
						) : (
							<>
								<h3 className='text-sm font-medium text-gray-700 mb-3'>
									{t('merchant.coupon.distribute.selectMembers')}
								</h3>
								<MemberSelectionList
									members={memberSelection.members}
									selectedUserIds={memberSelection.selectedUserIds}
									onToggleSelection={memberSelection.handleToggleSelection}
									isLoading={memberSelection.isLoading}
								/>
							</>
						)}

						{/* Summary - V1.1: One coupon per member */}
						{memberSelection.summary.totalMembersSelected > 0 && (
							<div className='mt-6 p-4 bg-blue-50 rounded-lg'>
								<p className='text-sm text-blue-700 font-medium'>
									{t('merchant.coupon.distribute.summaryV1_1', {
										count: memberSelection.summary.totalMembersSelected,
									})}
								</p>
							</div>
						)}

						{/* Validation Error */}
						{!memberSelection.isValid && memberSelection.members.length > 0 && (
							<p className='mt-4 text-sm text-red-600'>
								{t('merchant.coupon.distribute.validation.noMembersSelected')}
							</p>
						)}

						{/* Action Buttons */}
						<div className='mt-6 pt-4 border-t border-gray-200 space-y-3'>
							<button
								type='button'
								onClick={handleOpenConfirmation}
								disabled={!memberSelection.isValid || isSubmitting}
								className='w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base'
							>
								<Save className='w-4 h-4 mr-2' />
								{isSubmitting ? t('common.saving') : t('merchant.coupon.distribute.createAndDistribute')}
							</button>

							<button
								type='button'
								onClick={handleBackToCreate}
								className='w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm sm:text-base'
							>
								{t('merchant.coupon.distribute.backToEdit')}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Story 4.6: Confirmation Modal */}
			{showConfirmModal && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4'>
					<div className='bg-white rounded-xl shadow-xl max-w-md w-full p-6'>
						<h2 className='text-lg font-bold text-gray-900 mb-4'>
							{t('merchant.coupon.distribute.confirmTitle')}
						</h2>

						{/* Coupon Summary */}
						<div className='mb-6 p-4 bg-gray-50 rounded-lg'>
							<h3 className='font-medium text-gray-900 mb-2'>{formData.couponTitle}</h3>
							<p className='text-sm text-gray-600 mb-2'>{getDiscountPreview()}</p>
							{formData.couponExpiresAt && (
								<p className='text-xs text-gray-500'>
									{t('merchant.coupon.previewExpires', {
										date: formatExpiryForPreview(formData.couponExpiresAt)
									})}
								</p>
							)}
						</div>

						{/* Distribution Summary - V1.1: One coupon per member */}
						<div className='mb-6 p-4 bg-blue-50 rounded-lg'>
							<p className='text-sm text-blue-700 font-medium'>
								{t('merchant.coupon.distribute.summaryV1_1', {
									count: memberSelection.summary.totalMembersSelected,
								})}
							</p>
						</div>

						{/* Warning */}
						<p className='text-xs text-gray-500 mb-6'>
							{t('merchant.coupon.distribute.confirmWarning')}
						</p>

						{/* Actions */}
						<div className='flex gap-3'>
							<button
								type='button'
								onClick={handleCloseConfirmation}
								disabled={isSubmitting}
								className='flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50'
							>
								{t('common.cancel')}
							</button>
								<button
									type='button'
									onClick={() => {
										void submitCoupon();
									}}
									disabled={isSubmitting}
									className='flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium'
								>
								{isSubmitting ? (
									<>
										<div className='animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2' />
										{t('common.saving')}
									</>
								) : (
									<>
										<CheckCircle className='w-4 h-4 mr-2' />
										{t('merchant.coupon.distribute.confirmButton')}
									</>
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CouponCreationV2;
